// Direct-load /resolve prefetch. On a direct pair URL the lightweight route
// resolver (GET /v1/pairs/{ref}/resolve) is read-only and has no ordering
// contract with the seed barrier — the composed seed's subscribe-before-seed
// invariant and its pool_addr revalidation are untouched. Firing it from
// main.tsx (before React boots and before the PairPage chunk arrives) simply
// removes bundle-eval + chunk-load time from the resolve critical path.
//
// Consume-once with a short TTL: usePairFeed's resolve effect is the only
// consumer, an unconsumed or stale entry is discarded, and every later
// resolution (navigation, reconnect reseed) is a real network fetch. A stale
// pool answer is self-healing — the seed commit revalidates
// detail.pool_addr against the resolved route and re-runs on mismatch.

import { type PairResolvedRoute, type PairRouteMode, fetchPairResolvedRoute } from '../api/pairs'

const RESOLVE_PREFETCH_TTL_MS = 30_000
const MAX_PREFETCHES = 2

interface Entry {
  startedAt: number
  promise: Promise<PairResolvedRoute>
}

const entries = new Map<string, Entry>()

function resolveKey(addr: string, mode?: PairRouteMode): string {
  return JSON.stringify([addr, mode?.phase ?? '', mode?.source ?? '', mode?.dex ?? ''])
}

export function startPairResolvePrefetch(addr: string, mode?: PairRouteMode): void {
  if (!addr || addr === 'native') return
  const key = resolveKey(addr, mode)
  const now = Date.now()
  const existing = entries.get(key)
  if (existing && now - existing.startedAt < RESOLVE_PREFETCH_TTL_MS) return
  while (entries.size >= MAX_PREFETCHES) {
    const oldest = entries.keys().next().value
    if (oldest == null) break
    entries.delete(oldest)
  }
  const promise = fetchPairResolvedRoute(addr, { cache: 'no-store' }, mode)
  entries.set(key, { startedAt: now, promise })
  // An unconsumed rejection must never surface as an unhandled rejection.
  void promise.catch(() => undefined)
}

/** Non-consuming read of the in-flight/resolved prefetch for this exact
    (addr, mode): the entry stays for usePairFeed, the sole consumer. Module
    init chains that only need the resolved pool (early WS subscribe, early
    OHLCV) hook onto this promise instead of firing a second resolve. */
export function peekPairResolvePrefetch(
  addr: string,
  mode?: PairRouteMode
): Promise<PairResolvedRoute> | undefined {
  const entry = entries.get(resolveKey(addr, mode))
  if (!entry) return undefined
  if (Date.now() - entry.startedAt >= RESOLVE_PREFETCH_TTL_MS) return undefined
  return entry.promise
}

/** Returns the in-flight/resolved prefetch for this exact (addr, mode) and
    drops the entry either way, so a later mount or navigation re-resolves. */
export function consumePairResolvePrefetch(
  addr: string,
  mode?: PairRouteMode
): Promise<PairResolvedRoute> | undefined {
  const key = resolveKey(addr, mode)
  const entry = entries.get(key)
  if (!entry) return undefined
  entries.delete(key)
  if (Date.now() - entry.startedAt >= RESOLVE_PREFETCH_TTL_MS) return undefined
  return entry.promise
}

export function clearPairResolvePrefetchForTest(): void {
  entries.clear()
}
